import {
  adminPasswordResetRequiredKey,
  portalLoginRoutePath,
  userPasswordResetRequiredKey,
} from '@/lib/auth/portal-routes'
import {
  clearStoredSessionSource,
  clearStoredRefreshToken,
  clearStoredToken,
  setStoredSessionSource,
  setStoredRefreshToken,
  setStoredToken,
  type PortalSessionSource,
  type PortalKind,
} from '@/lib/auth/token-storage'
import {
  appendUserLoginResumeMarker,
  normalizeUserPostLoginReturnPath,
  persistUserPostLoginReturnPath,
} from '@/lib/auth/session-storage'

export const portalSessionExpiredEventName = 'portal-session-expired'

/**
 * 登录和 refresh 成功后共享的 session payload。
 * 后端字段使用 snake_case，持久化前不在 UI 层改名，避免和 API contract 脱节。
 */
export interface PortalAuthPayload {
  password_reset_required?: boolean
  refresh_token?: string | null
  token: string
  user: unknown
}

export interface PortalSessionExpiredDetail {
  portal: PortalKind
}

export interface PersistPortalSessionOptions {
  source?: PortalSessionSource
}

const portalPasswordResetRequiredKey: Record<PortalKind, string> = {
  admin: adminPasswordResetRequiredKey,
  user: userPasswordResetRequiredKey,
}

/**
 * 持久化门户 session。
 * access token、refresh token、用户快照和强制改密标记必须一起更新，避免半登录状态。
 */
export function persistPortalSession(
  portal: PortalKind,
  payload: PortalAuthPayload,
  options: PersistPortalSessionOptions = {},
) {
  setStoredToken(portal, payload.token)
  setStoredSessionSource(portal, options.source ?? 'password')
  if (payload.refresh_token) {
    setStoredRefreshToken(portal, payload.refresh_token)
  } else {
    clearStoredRefreshToken(portal)
  }

  window.localStorage.setItem(`${portal}_data`, JSON.stringify(payload.user))

  if (payload.password_reset_required) {
    window.localStorage.setItem(portalPasswordResetRequiredKey[portal], 'true')
    return
  }

  window.localStorage.removeItem(portalPasswordResetRequiredKey[portal])
}

/**
 * 清理门户所有本地认证状态。
 * 前台和后台使用不同 key，不能跨门户清理另一侧 session。
 */
export function clearPortalSession(portal: PortalKind) {
  clearStoredToken(portal)
  clearStoredRefreshToken(portal)
  clearStoredSessionSource(portal)
  window.localStorage.removeItem(`${portal}_data`)
  window.localStorage.removeItem(portalPasswordResetRequiredKey[portal])
}

/**
 * 通知 shell 和页面层 session 已不可恢复。
 * 请求层不直接跳转，避免在数据请求 helper 里隐藏导航副作用。
 */
export function notifyPortalSessionExpired(portal: PortalKind) {
  window.dispatchEvent(
    new CustomEvent<PortalSessionExpiredDetail>(portalSessionExpiredEventName, {
      detail: { portal },
    }),
  )
}

/**
 * 最终跳转动作集中在这里，便于后续替换为 router navigation。
 */
export function resolvePortalLoginRedirectPath(portal: PortalKind, currentPath: string) {
  const loginPath = portalLoginRoutePath[portal]
  if (portal !== 'user') {
    return loginPath
  }

  const returnPath = normalizeUserPostLoginReturnPath(currentPath)
  if (!returnPath) {
    return loginPath
  }
  persistUserPostLoginReturnPath(returnPath)
  return appendUserLoginResumeMarker(loginPath)
}

export function redirectToPortalLogin(portal: PortalKind) {
  window.location.assign(resolvePortalLoginRedirectPath(portal, window.location.pathname))
}
